import { cn } from '@invent/wl-ui-kit-next';
import { Tooltip, TooltipContent, TooltipTrigger } from '@invent/wl-ui-kit-next/ui';
import { ArrowDown, ArrowUp } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { DataTable, NewTabLink, type DataTableState, type IDataColumn, type IDataSelection } from '@/design';
import type {
  IListAggregates,
  IListField,
  IListFormattingRule,
  IListOrder,
  IListQuery,
  IListRow,
  ListFieldSource
} from '../../api/lists-contracts';
import { NAME_FIELD, type ICriteriaGroup, type ICriteriaWords, type KindOf } from '../../list-criteria';
import { recordSurveyPath } from '../../components/participant-link';
import { fieldSourceNote, formatListValue, listSurveyLabel, provenanceDraws } from '../../lists';
import {
  CellValue,
  EditableCell,
  cellFormatting,
  formattingClass,
  rowFormatting,
  type ICellEdit
} from './cells';
import { editKindOf, nameOf } from './editing';
import { Funnel } from './funnel';
import { aggregateGroupOf, dragProps, groupLevels } from './group-by';
import { rowKeyOf } from './use-list-rows';

/**
 * A list's grid (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §3.5): the design system's `DataTable` — sticky header, sideways scroll past `minWidth`, one line
 * a cell, the keyboard walking the rows — over the view's columns in their order. **A header is
 * label · funnel**: a press on the label cycles the sort ascending → descending → off (survey-api
 * sorts, blanks last), its tooltip says the field's question number and source, and the header is
 * dragged onto the group strip where its field groups. A column whose kind filters nothing says
 * "Not filterable" in the funnel's place.
 *
 * **A row survey-api says left is struck**, its first cell saying "no longer matches this list" —
 * the server does not say which condition, and the screen evaluates none (§11.2). **The groups'
 * headings count and sum the whole result** (`/aggregates`), the rows under them the page's.
 *
 * **The writes** (§3.6, §3.9, §3.10): the selection column (`selectable`, for *Actions ▾*), the
 * **Action** column — *View* · *Edit*, the reading pane in either mode, *Note*, the row's staff notes
 * (SR-491), and an arrow opening the row's survey on its record page in a new tab — and on an editable column
 * the cell's control (`EditableCell`), staging a draft until *Save data*. A row's press or Enter
 * opens the pane too; the row in it is marked (`aria-current`).
 */

/** The Action column's presses, and why *Edit* is not offered where it is not. */
export interface IRowActions {
  onView: (row: IListRow) => void;
  onEdit: (row: IListRow) => void;
  /** *Note* — the row's notes, and a box to add one (SR-491). */
  onNote: (row: IListRow) => void;
  /** `NotPermitted`'s id where the reader's roles do not reach an edit — drawn once on the screen. */
  editLocked: Nullable<string>;
}

type RowProps = React.HTMLAttributes<HTMLTableRowElement> & Record<`data-${string}`, string | undefined>;

/** The sort a press on a column's label asks for: ascending → descending → off. One column sorts
 *  at a time, Conga's way; a press on another column starts it ascending. */
export function nextSorts(sorts: readonly IListOrder[], field: string): IListOrder[] {
  const [first] = sorts;
  if (first?.field !== field) return [{ field, dir: 'asc' }];
  return first.dir === 'desc' ? [] : [{ field, dir: 'desc' }];
}

/** A column's header: the label's press sorts, its tooltip says the source, then the funnel —
 *  exported for the work pages' tables (`work-page/`). */
export function Header({
  field,
  source,
  sorts,
  onSort,
  funnel
}: {
  field: IListField;
  source: ListFieldSource | undefined;
  sorts: readonly IListOrder[];
  onSort: (sorts: IListOrder[]) => void;
  funnel: React.ReactNode;
}) {
  const sorted = sorts[0]?.field === field.code ? (sorts[0].dir === 'desc' ? 'desc' : 'asc') : null;
  const Arrow = sorted === 'desc' ? ArrowDown : ArrowUp;
  const note = [
    // "Not corrected in a list" goes by whether the grid edits it — a tag this app has no control
    // for included — not by the catalogue's flag alone.
    source ? fieldSourceNote({ ...field, editable: editKindOf(field) !== null }, source) : null,
    field.sortable ? null : 'not sortable',
    field.groupable ? 'drag onto the strip above to group by it' : null
  ]
    .filter(Boolean)
    .join(' · ');
  // SR-420's AC2: a profile column says what it is filled from in each survey, a line per survey.
  const draws = provenanceDraws(field.provenance);
  return (
    <span
      data-column={field.code}
      {...dragProps(field)}
      className={cn('inline-flex items-center gap-0.5', field.groupable && 'cursor-grab')}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`${field.label}${sorted ? `, sorted ${sorted === 'asc' ? 'ascending' : 'descending'}` : ''}`}
            aria-disabled={field.sortable ? undefined : true}
            onClick={() => {
              if (field.sortable) onSort(nextSorts(sorts, field.code));
            }}
            className="inline-flex items-center gap-1 rounded px-0.5 outline-none hover:text-[var(--color-royal-blue)] focus-visible:ring-2 focus-visible:ring-ring"
          >
            {field.number && (
              <span className="tabular-nums font-normal text-muted-foreground">{field.number}</span>
            )}
            {field.label}
            <Arrow className={cn('size-3', sorted ? 'opacity-100' : 'opacity-0')} aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {note}
          {draws.map((draw) => (
            <span key={draw} className="block">
              {draw}
            </span>
          ))}
        </TooltipContent>
      </Tooltip>
      {funnel}
    </span>
  );
}

/** A column's funnel open — by its own button, or by a filter chip, which takes the focus back. */
export interface IFunnelOpen {
  code: string;
  returnTo: Nullable<HTMLElement>;
}

/** The grid's funnels: what applying one does, *Advanced…*, and which is open — the screen's, so
 *  a filter chip's press can open its column's (§3.4). */
export interface IGridFiltering {
  onApply: (root: ICriteriaGroup) => void;
  onAdvanced: () => void;
  open: Nullable<IFunnelOpen>;
  onOpen: (open: Nullable<IFunnelOpen>) => void;
}

export function ListGrid({
  label,
  columns,
  fieldOf,
  sourceOf,
  rows,
  state,
  struck,
  sorts,
  onSort,
  root,
  query,
  kindOf,
  words,
  filtering,
  groupBy,
  collapsed,
  onToggleGroup,
  aggregates,
  sums,
  rules,
  editOf,
  selection,
  actions,
  open
}: {
  label: string;
  /** The view's columns, in order — those the catalogue does not name are the screen's to say. */
  columns: readonly IListField[];
  fieldOf: (code: string) => IListField | undefined;
  sourceOf: (code: string) => ListFieldSource | undefined;
  /** Null while there is nothing to draw — `state` says why. */
  rows: Nullable<IListRow[]>;
  state?: DataTableState;
  /** The participants survey-api says left the list since the page was read. */
  struck: ReadonlySet<string>;
  sorts: readonly IListOrder[];
  onSort: (sorts: IListOrder[]) => void;
  root: ICriteriaGroup;
  /** The read's query — what a funnel's values are counted under. */
  query: IListQuery;
  kindOf: KindOf;
  words: ICriteriaWords;
  /** The headers' funnels — absent, none is drawn (a report's preview, §4.2). */
  filtering?: IGridFiltering;
  groupBy: readonly IListOrder[];
  collapsed: ReadonlySet<string>;
  onToggleGroup: (key: string) => void;
  aggregates: Nullable<IListAggregates>;
  /** The summable columns shown — a heading's sums. */
  sums: readonly IListField[];
  rules: readonly IListFormattingRule[];
  /** A cell's edit, or null where it is read-only to this reader. */
  editOf?: (row: IListRow, field: IListField) => Nullable<ICellEdit>;
  selection?: IDataSelection<IListRow>;
  actions?: IRowActions;
  /** The row the pane reads — opened by a press on the row, marked. */
  open?: { key: Nullable<string>; onOpen: (row: IListRow) => void };
}) {
  const kind = (code: string) => fieldOf(code)?.kind;
  const firstColumn = columns[0]?.code;

  const dataColumns: IDataColumn<IListRow>[] = columns.map((field) => ({
    id: field.code,
    label: field.label,
    numeric: field.kind === 'number' || field.kind === 'money' || field.kind === 'percent',
    // `relative`: a cell's editor lies over the cell (`EditableCell`).
    className: 'relative whitespace-nowrap',
    header: (
      <Header
        field={field}
        source={sourceOf(field.code)}
        sorts={sorts}
        onSort={onSort}
        funnel={
          filtering && (
            <Funnel
              field={field}
              root={root}
              query={query}
              kindOf={kindOf}
              words={words}
              onApply={filtering.onApply}
              onAdvanced={filtering.onAdvanced}
              open={filtering.open?.code === field.code}
              onOpenChange={(open) =>
                filtering.onOpen(open ? { code: field.code, returnTo: null } : null)
              }
              returnTo={filtering.open?.code === field.code ? filtering.open.returnTo : null}
            />
          )
        }
      />
    ),
    cell: (row) => {
      const gone = struck.has(row.participantId);
      const edit = editOf?.(row, field) ?? null;
      const look = cellFormatting(rules, row, field.code, kind);
      return (
        <span className="inline-flex max-w-full items-baseline gap-2">
          <span className={cn('min-w-0', gone && 'line-through opacity-70')}>
            {edit ? (
              <EditableCell field={field} row={row} name={nameOf(row)} edit={edit} look={look} />
            ) : actions && field.code === NAME_FIELD ? (
              // The name is a real link to the record (SR-491): Ctrl/Cmd-click and the middle button
              // open it in a new tab; a plain click reads the row in the pane, as a row's press does.
              <Link
                to={recordSurveyPath(row.participantId, row.survey)}
                className="hover:underline"
                onClick={(event) => {
                  event.stopPropagation();
                  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                    return;
                  }
                  event.preventDefault();
                  // The row's own press, the pane's mode kept.
                  if (open) open.onOpen(row);
                  else actions.onView(row);
                }}
              >
                <CellValue field={field} cell={row.cells[field.code]} look={look} />
              </Link>
            ) : (
              <CellValue field={field} cell={row.cells[field.code]} look={look} />
            )}
          </span>
          {gone && field.code === firstColumn && (
            <span className="text-xs text-muted-foreground">— no longer matches this list</span>
          )}
        </span>
      );
    }
  }));

  const actionColumn: IDataColumn<IListRow>[] = actions
    ? [
        {
          id: '__action',
          label: 'Action',
          header: 'Action',
          className: 'whitespace-nowrap',
          cell: (row) => {
            const name = nameOf(row);
            const locked = actions.editLocked;
            return (
              <span className="inline-flex items-center gap-1 text-[0.8125rem]">
                <button
                  type="button"
                  aria-label={`View ${name}`}
                  onClick={() => actions.onView(row)}
                  className="rounded px-0.5 font-medium text-[var(--color-sapphire)] outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
                >
                  View
                </button>
                <span aria-hidden className="text-muted-foreground">
                  ·
                </span>
                <button
                  type="button"
                  aria-label={`Edit ${name}`}
                  aria-disabled={locked !== null ? true : undefined}
                  aria-describedby={locked ?? undefined}
                  onClick={() => {
                    if (locked === null) actions.onEdit(row);
                  }}
                  className={cn(
                    'rounded px-0.5 font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    locked === null
                      ? 'text-[var(--color-sapphire)] hover:underline'
                      : 'cursor-not-allowed text-muted-foreground'
                  )}
                >
                  Edit
                </button>
                <span aria-hidden className="text-muted-foreground">
                  ·
                </span>
                <button
                  type="button"
                  aria-label={`Notes on ${name}`}
                  onClick={() => actions.onNote(row)}
                  className="rounded px-0.5 font-medium text-[var(--color-sapphire)] outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
                >
                  Note
                </button>
                <NewTabLink
                  to={recordSurveyPath(row.participantId, row.survey)}
                  label={`Open ${name} — ${listSurveyLabel(row.survey)} in a new tab`}
                />
              </span>
            );
          }
        }
      ]
    : [];

  const headingCount = (path: readonly string[]) => {
    const group = aggregateGroupOf(aggregates, path);
    if (group === undefined) return null;
    const summed = sums
      .map((field) => {
        const sum = group.sums[field.code];
        return sum === null || sum === undefined ? null : `Σ ${field.label} ${formatListValue(field, sum)}`;
      })
      .filter(Boolean);
    return `(${group.count.toLocaleString('en-US')})${summed.length > 0 ? ` · ${summed.join(' · ')}` : ''}`;
  };

  return (
    <DataTable
      label={label}
      columns={[...actionColumn, ...dataColumns]}
      rows={rows}
      rowKey={rowKeyOf}
      selectable={selection}
      onOpen={open?.onOpen}
      selected={open?.key ?? null}
      rowProps={(row): RowProps => {
        const look = rowFormatting(rules, row, kind);
        return {
          'data-participant': row.participantId,
          'data-row-key': rowKeyOf(row),
          'data-struck': struck.has(row.participantId) ? '' : undefined,
          className: formattingClass(look)
        };
      }}
      minWidth={`${Math.max(48, columns.length * 10 + (actions ? 9 : 0))}rem`}
      grouping={
        groupBy.length > 0
          ? {
              levels: groupLevels(groupBy, fieldOf, rows ?? [], words.personOf),
              collapsed,
              onToggle: onToggleGroup,
              count: (path, under) => headingCount(path) ?? `(${under.length} on this page)`
            }
          : undefined
      }
      state={state}
    />
  );
}
